/**
 * To phase out adding things to string prototype there will be some duplication here with the stringPrototype.js.
 * Defining these are simple functions makes them easier to test and debug because you know where the function
 * comes from.
 */

/**
 * indexOf returns the index of the first occurrence of a string within a string.
 * This returns the indices of all of the occurrences.
 *
 * @param {RegExp} regex
 * @param {string} str
 */
const indexOfAll = (regex, str) => {
    regex = new RegExp(regex, 'g');
    let indexes = [];
    let match;
    // eslint-disable-next-line no-cond-assign
    while ((match = regex.exec(str)) !== null) {
        indexes.push(match.index);
    }

    return indexes;
};

/**
 * Change the first letter of a string to lowercase. The rest of the string is not modified.
 *
 * @param {string} str
 * @return {string}
 */
const lcfirst = function (str) {
    return str.substring(0, 1).toLowerCase() + str.substring(1);
};

/**
 * Change the first letter of a string to uppercase. The rest of the string is not modified.
 *
 * @param {string} str
 * @return {string}
 */
const ucfirst = function (str) {
    return str.substring(0, 1).toUpperCase() + str.substring(1);
};

/**
 * @param {string} str
 * @returns {string}
 */
const ucwords = (str) => (str + '').replace(/^([a-z])|\s+([a-z])/g, ($1) => $1.toUpperCase());

/**
 * Convert A String To "Title Case".
 *
 * @param {string} str
 * @return {string}
 */
const toTitleCase = (str) => str.replace(
    /\w\S*/g,
    (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
);

/**
 * Convert a string "LIKE_THIS" to "like this".
 *
 * @param {string} str
 * @returns {string}
 */
const fromUpperSnakeCase = (str) => str.replace(/_/gi, ' ').toLowerCase();

/**
 * Convert a string "like this" to "LIKE_THIS".
 *
 * @param {string} str
 * @returns {string}
 */
const toUpperSnakeCase = (str) => str.replace(/ /gi, '_').toUpperCase();

/**
 * Convert a string "like_this" or "like this" to "likeThis".
 *
 * @param {string} str
 * @returns {string}
 */
const toCamelCase = (str) => {
    if (!str.includes('_') && !str.includes(' ')) {
        return str;
    }

    return lcfirst(str.split(/[ _]/gi)
        .map((s) => ucfirst(s.toLowerCase()))
        .join(''));
};

/**
 * Convert a string "likeThis" to "like_this".
 *
 * @param {string} str
 * @returns {string}
 */
const toSnakeCase = (str) => str.replace(/([a-zA-Z])(?=[A-Z])/g, '$1_').toLowerCase();

/**
 * Convert a camelCase (or PascalCase) string "likeThis" to "like This".
 * Chain or combine with other string functions if you want it "Like this" or "Like This" etc.
 *
 * @param {string} str
 *
 * @return {string}
 */
const splitCamelCase = (str) => str.replace(/([a-z])([A-Z])/g, '$1 $2');

/**
 * Convert a string to a version that is better for comparison when filtering.
 *
 * This returns an object with two properties:
 * - string: The original string converted to lowercase. Initially the idea was to reduce this to only alphanumeric
 *   characters, but that would break non-English item names.
 * - words: The input string broken up into separate words, each converted to lowercase and reduced to alphanumeric
 *   characters. This allows you to find a ticket called "Adult's Open Ticket" by searching for "open adult".
 *
 * @param {string} str
 *
 * @return {{string: string, words: string[]}}
 */
const normalizeSearchQuery = (str) => {
    // Remove common symbols that would make searching harder and make string lowercase.
    const localStr = str.replace(/['&]/gi, '').toLowerCase();

    return {
        // Normalise the string by removing spaces.
        string: localStr.replace(/[\s]/gi, ''),

        // Split up the string into words and remove any non-alphanumeric characters.
        words: localStr.replace(/[^a-z0-9\s]/gi, '').split(' ').filter(Boolean),
    };
};

/**
 * Repeat a string 'num' times.
 *
 * @param {string} str
 * @param {number} num
 */
const repeat = (str, num) => {
    return new Array(1 + num).join(str);
};

/**
 * Compare two string to see if they match, for the purposes of filtering items by name.
 * The filterQuery should have already been normalized.
 *
 * @param {string} haystack
 * @param {{string: string, words: string[]}} needle
 *
 * @return {boolean}
 */
const compareStringsForFilter = (haystack, needle) => {
    if (!haystack) {
        return false;
    }

    const normalizedHaystack = normalizeSearchQuery(haystack);

    // First try to simply see if the target string contains the search query.
    if (normalizedHaystack.string.indexOf(needle.string) !== -1) {
        return true;
    }

    // If that fails, check if all the words in the search query are in the target string.
    for (let i = 0; i < needle.words.length; i++) {
        if (normalizedHaystack.string.indexOf(needle.words[i]) === -1) {
            // The target string is missing one of the words in the search query, so it's not a match.
            return false;
        }
    }

    // The target string must contain all the words in the search query - it's a match.
    return true;
};

const alphabet = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'];

/**
 * Case-insensitive string comparison using localeCompare
 *
 * @param {string} a - First string
 * @param {string} b - Second string
 * @returns {number} Comparison result (-1, 0, 1)
 */
const compareStrings = (a, b) => {
    return String(a).localeCompare(String(b), undefined, {
        sensitivity: 'base',
        numeric: true,
    });
};

module.exports = {
    alphabet,
    compareStringsForFilter,
    fromUpperSnakeCase,
    indexOfAll,
    normalizeSearchQuery,
    repeat,
    splitCamelCase,
    toCamelCase,
    toSnakeCase,
    toTitleCase,
    toUpperSnakeCase,
    ucfirst,
    ucwords,
    compareStrings,
};
